/* Fan info — field schema and the draft ↔ account mapping.
   The two lists (TG Notifications, OF Comment) pick from the same 30 fields.
   Fan analysis has no switch of its own: it's on whenever either list is on. */

import type { Account } from '../../../../api/client';

export type ListKey = 'notif' | 'comments';

/** Field keys match the backend allowed_* array values; `label` is the key
 *  under chatAssistant:fanInfo.fields. */
export interface FieldGroup { id: string; fields: { key: string; label: string }[] }

export const FIELD_GROUPS: FieldGroup[] = [
  {
    id: 'identity',
    fields: [
      { key: 'nickname', label: 'nickname' },
      { key: 'names_fan_calls_model', label: 'namesFanCallsModel' },
      { key: 'real_first_name', label: 'realFirstName' },
      { key: 'real_surname', label: 'realSurname' },
      { key: 'age', label: 'age' },
      { key: 'date_of_birth', label: 'dateOfBirth' },
      { key: 'nationality', label: 'nationality' },
    ],
  },
  {
    id: 'background',
    fields: [
      { key: 'location_country', label: 'locationCountry' },
      { key: 'location_city', label: 'locationCity' },
      { key: 'family_status', label: 'familyStatus' },
      { key: 'family_information', label: 'familyInformation' },
      { key: 'hobbies_and_interests', label: 'hobbiesAndInterests' },
      { key: 'reason_to_join_onlyfans', label: 'reasonToJoinOnlyfans' },
    ],
  },
  {
    id: 'engagement',
    fields: [
      { key: 'fan_type', label: 'fanType' },
      { key: 'fan_current_mood', label: 'fanCurrentMood' },
      { key: 'chat_summary', label: 'chatSummary' },
      { key: 'chat_recommendation', label: 'chatRecommendation' },
      { key: 'inside_jokes_and_slang', label: 'insideJokesAndSlang' },
    ],
  },
  {
    id: 'fetish',
    fields: [
      { key: 'sex_fetishes_woman_body_part', label: 'bodyPart' },
      { key: 'sex_fetishes_positions', label: 'positions' },
      { key: 'sex_fetishes_emotional_tone', label: 'emotionalTone' },
      { key: 'sex_fetishes_roleplay_for_boy', label: 'roleplayBoy' },
      { key: 'sex_fetishes_roleplay_for_girl', label: 'roleplayGirl' },
      { key: 'sex_fetishes_additional_details', label: 'fetishAdditionalDetails' },
      { key: 'sex_fetishes_brief_summary', label: 'briefSummary' },
      { key: 'sex_fetishes_sensory_touch', label: 'sensoryTouch' },
      { key: 'sex_fetishes_sensory_temperature', label: 'sensoryTemperature' },
      { key: 'sex_fetishes_sensory_movement', label: 'sensoryMovement' },
      { key: 'sex_fetishes_sensory_sound_from_girl', label: 'sensorySoundFromGirl' },
    ],
  },
  { id: 'other', fields: [{ key: 'additional_details', label: 'additionalDetails' }] },
];

export const ALL_FIELDS = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));

const DEFAULT_NOTIF = ['nickname', 'names_fan_calls_model', 'real_first_name', 'age', 'location_country', 'fan_type', 'chat_summary', 'chat_recommendation', 'inside_jokes_and_slang'];
const DEFAULT_COMMENTS = ['nickname', 'names_fan_calls_model', 'real_first_name', 'age', 'location_country', 'location_city', 'fan_type', 'fan_current_mood', 'inside_jokes_and_slang'];

export interface Draft {
  on: Record<ListKey, boolean>;
  fields: Record<ListKey, string[]>;
  overwrite: boolean;
}

/** Keep only known fields, in schema order, so picks compare and save the same
 *  whatever order they were clicked in. */
export const normalise = (keys: string[]) => ALL_FIELDS.filter((k) => keys.includes(k));

/** A list only counts as on when analysis was on too (analysis used to gate both). */
export function draftOf(a: Account): Draft {
  const analysis = a.is_user_info_short_analysis_allowed ?? true;
  return {
    on: {
      notif: analysis && (a.is_fan_info_in_notifications_allowed ?? true),
      comments: analysis && (a.is_comment_updating_allowed ?? true),
    },
    fields: {
      notif: normalise(a.allowed_fan_info_notification_fields ?? DEFAULT_NOTIF),
      comments: normalise(a.allowed_comment_fields ?? DEFAULT_COMMENTS),
    },
    overwrite: a.is_comment_overwrite_allowed ?? false,
  };
}

export type Payload = Pick<Account,
  'is_user_info_short_analysis_allowed' | 'is_fan_info_in_notifications_allowed' | 'allowed_fan_info_notification_fields' |
  'is_comment_updating_allowed' | 'is_comment_overwrite_allowed' | 'allowed_comment_fields'>;

export function payloadOf(d: Draft): Payload {
  return {
    is_user_info_short_analysis_allowed: d.on.notif || d.on.comments,
    is_fan_info_in_notifications_allowed: d.on.notif,
    allowed_fan_info_notification_fields: d.fields.notif,
    is_comment_updating_allowed: d.on.comments,
    is_comment_overwrite_allowed: d.overwrite,
    allowed_comment_fields: d.fields.comments,
  };
}

/** What the account has stored right now, in payload shape — the baseline the
 *  first save diffs against (so a stale analysis flag gets corrected). */
export function storedOf(a: Account): Record<string, unknown> {
  return {
    is_user_info_short_analysis_allowed: a.is_user_info_short_analysis_allowed ?? true,
    is_fan_info_in_notifications_allowed: a.is_fan_info_in_notifications_allowed ?? true,
    allowed_fan_info_notification_fields: a.allowed_fan_info_notification_fields ?? null,
    is_comment_updating_allowed: a.is_comment_updating_allowed ?? true,
    is_comment_overwrite_allowed: a.is_comment_overwrite_allowed ?? false,
    allowed_comment_fields: a.allowed_comment_fields ?? null,
  };
}
